import { PromptElementImplInterface } from './PromptElementImplInterface';
import { DataStateChangeEvent, PromptElement, RangeSliderController } from '@sas-dvr/ltjs-bird';
import { ReportState } from '@sas-dvr/internal-va-react-core/store/rootReducer';
import {
  SliderData,
  SliderState,
} from '@sas-dvr/internal-va-react-core/store/birdElement/sliderState';
import {
  filter,
  tap,
  ignoreElements,
  map,
  mergeMap,
  distinctUntilChanged,
  skipWhile,
  share,
} from 'rxjs/operators';
import { Observable, EMPTY, of, merge } from 'rxjs';
import {
  MissingValueBehavior,
  MissingValueCheckboxLocationType,
  PortableEvent,
} from '@sas-dvr/ltjs-commons';
import { StateObservable } from 'redux-observable';
import { AnyAction } from 'redux';
import { ReportContextMenuState } from '@sas-dvr/internal-va-react-core/store/reportContextMenu/ReportContextMenuState';
import { ContextMenuItems } from '../../../bird/util/contextMenu';

function getData(controller: RangeSliderController, dualRange: boolean): SliderData {
  const defaultRange = controller.getDefaultRange();
  const currentDataRange = controller.getCurrentDataRange() ?? defaultRange ?? [0, 0];

  let currentSelectedRange = controller.getCurrentSelectedRange();
  if (
    !currentSelectedRange ||
    // currentSelectedRange may contain NaN while processing initial value parameters
    Number.isNaN(currentSelectedRange[0]) ||
    (dualRange && Number.isNaN(currentSelectedRange[1]))
  ) {
    currentSelectedRange = defaultRange ?? currentDataRange;
  }

  const values = {
    min: currentSelectedRange[0],
    max: dualRange ? currentSelectedRange[1] : undefined,
  };
  const baseModel = controller.getBIRDModel();
  const model = baseModel instanceof PromptElement ? baseModel : undefined;
  return {
    name: model?.getLabel() || '',
    valueColumnLabel: controller.getLabel() || '',
    values,
    sliderState: values,
    range: { min: currentDataRange[0], max: currentDataRange[1] },
    format: controller.getFormatName() || undefined,
    horizontal: controller.isHorizontal(),
    missingValueCheckboxLocation:
      controller.getMissingValueCheckboxLocation() === MissingValueCheckboxLocationType.BOTTOM
        ? 'bottom'
        : 'none',
    missingValueBehavior:
      controller.getMissingValueBehavior() === MissingValueBehavior.INCLUDE ? 'include' : 'default',
  };
}

export class SliderImpl implements PromptElementImplInterface {
  private _controller: RangeSliderController;
  private _id: string;
  private _dualRange: boolean;
  private _ignoreValueChange: boolean = false;

  constructor(controller: RangeSliderController, id: string, dualRange: boolean) {
    this._controller = controller;
    this._id = id;
    this._dualRange = dualRange;
  }

  createElement() {
    return SliderState.actions.add(this._id, {
      ...getData(this._controller, this._dualRange),
    });
  }

  deleteElement() {
    return SliderState.actions.delete(this._id);
  }

  handleDataChange(dataChange$: Observable<DataStateChangeEvent>) {
    return dataChange$.pipe(
      map(() => {
        return SliderState.actions.update(this._id, getData(this._controller, this._dualRange));
      })
    );
  }

  handleValueChanged(valueChange$: Observable<PortableEvent>) {
    return valueChange$.pipe(
      mergeMap(() => {
        if (this._ignoreValueChange) {
          return EMPTY;
        }
        return of(SliderState.actions.update(this._id, getData(this._controller, this._dualRange)));
      })
    );
  }

  handleActions(_myActions$: Observable<AnyAction>, state$: StateObservable<ReportState>) {
    const isContextMenuTarget$ = state$.pipe(
      map((state) => {
        const contextMenuTarget = state.ui.reportContextMenu.target?.id;
        return !!contextMenuTarget && contextMenuTarget === this._id;
      }),
      distinctUntilChanged(),
      share()
    );
    return merge(
      state$.pipe(
        map((aState) => aState.birdElements.Slider[this._id]),
        filter((myState): myState is Exclude<typeof myState, undefined> => myState !== undefined),
        map((myState) => myState.values),
        skipWhile(() => {
          // Avoid setting the selected value until the controller gets a valid range
          const currentDataRange = this._controller.getCurrentDataRange();
          return (
            !currentDataRange ||
            currentDataRange.every((val) => val === 0) ||
            currentDataRange.some((val) => Number.isNaN(val))
          );
        }),
        distinctUntilChanged((a, b) => {
          return a.min === b.min && a.max === b.max;
        }),
        tap((values) => {
          this._ignoreValueChange = true;
          if (this._dualRange) {
            this._controller.setCurrentSelectedRange(values.min, values.max || 0);
          } else {
            this._controller.setCurrentSelectedValue(values.min);
          }
          this._ignoreValueChange = false;
        }),
        ignoreElements()
      ),
      state$.pipe(
        map((aState) => aState.birdElements.Slider[this._id]?.missingValueBehavior),
        distinctUntilChanged(),
        tap((missingValueBehavior) => {
          if (missingValueBehavior) {
            this._controller.setMissingValueBehavior(
              missingValueBehavior === 'default'
                ? MissingValueBehavior.DEFAULT
                : MissingValueBehavior.INCLUDE,
              true
            );
          }
        }),
        ignoreElements()
      ),
      isContextMenuTarget$.pipe(
        filter((isTarget) => isTarget),
        filter(() => this._controller.getShowIncludeMissingValuesInObjectMenu()),
        map(() => {
          return ReportContextMenuState.actions.setObjectItem(ContextMenuItems.INCLUDE_MISSING, {
            text: { key: 'ContextMenu.includeMissingValues.txt' },
            action: SliderState.actions.update(this._id, {
              missingValueBehavior:
                state$.value.birdElements.Slider[this._id]?.missingValueBehavior === 'default'
                  ? 'include'
                  : 'default',
            }),
            checked: state$.value.birdElements.Slider[this._id]?.missingValueBehavior === 'include',
          });
        })
      )
    );
  }
}
